import React, { FC, useEffect, useState } from 'react'
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { RulesConfigurationFormType, Step, StepDirection, steps } from '../../types/types';
import { Button } from '@vadiun/react-components';
import clsx from 'clsx';
import TravelStatusStep from './TravelStatusStep';
import { useServiceTypes } from 'shared/hooks/useServiceTypes';
import { useStagesOfReserveStates } from 'shared/hooks/useStagesOfReserveStates';
import { usePolygonZones } from 'shared/hooks/usePolygonZones';
import GrouperAndServiceTypeStep from './GrouperAndServiceTypeStep';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Typography } from '@material-ui/core';
import { useChannelTypes } from 'shared/hooks/useChannelTypes';

const validationSchema = Yup.object().shape({
    service_type_ids: Yup.array()
        .min(1, 'Seleccioná al menos un tipo de servicio')
        .required('Requerido'),
    is_moment: Yup.boolean(),
    is_priority: Yup.boolean(),
    airport: Yup.boolean(),
    stages: Yup.array().min(1, 'Debe haber al menos una configuración de semáforo').of(Yup.object().shape({
        stage: Yup.object().shape({
            id: Yup.number().nullable(),
            key: Yup.string().nullable(),
            from_state: Yup.object().shape({
                id: Yup.number().nullable(),
                key: Yup.string().nullable(),
                name: Yup.string().nullable(),
            }),
            to_state: Yup.object().shape({
                id: Yup.number().nullable(),
                key: Yup.string().nullable(),
                name: Yup.string().nullable(),
            }),
            description: Yup.string().nullable(),
        }),
        has_polygon: Yup.boolean(),
        is_countdown: Yup.boolean(),
        semaphores: Yup.array().when('has_polygon', {
            is: true,
            then: (schema) => schema.min(1, 'Si hay polígono, se requiere al menos un semáforo'),
            otherwise: (schema) => schema.min(1, 'Se requiere al menos un semáforo'),
        }).of(
            Yup.object().shape({
                polygon_zone_id: Yup.number().nullable(),
                semaphore_levels: Yup.array().of(Yup.object().shape({
                    semaphore_color_key: Yup.string().required('Requerido'),
                    threshold_minutes: Yup.number().typeError('Número inválido').min(0, 'Min 0').required('Requerido'),
                    semaphore_message_templates: Yup.object().shape({
                        id: Yup.number().nullable(),
                        channel_keys: Yup.array().nullable(),
                        title: Yup.string().nullable(),
                        body: Yup.string().nullable(),
                    })
                })),
            })).min(1, 'Debe haber al menos una configuración de semáforo')
    }))
})

interface RulesConfigurationFormProps {
    initialData?: RulesConfigurationFormType | null;
    onSubmit?: (values: RulesConfigurationFormType) => Promise<void> | void;
    readOnly?: boolean;
}

const RulesConfigurationForm: FC<RulesConfigurationFormProps> = ({
    initialData = null,
    onSubmit,
    readOnly = false
}) => {
    const {
        serviceTypes: {
            data: servicesOptions,
            error: serviceTypesError,
            reload: reloadServiceTypes,
            isLoading: serviceTypesLoading,
        }
    } = useServiceTypes();
    const {
        stagesOfReserveStates: {
            data: stagesOfReserveStatesOptions,
            error: stagesError,
            reload: reloadStages,
            isLoading: stagesLoading,
        }
    } = useStagesOfReserveStates();
    const {
        polygonZones: {
            data: polygonZonesOptions,
            error: polygonZonesError,
            reload: reloadPolygonZones,
            isLoading: polygonZonesLoading,
        }
    } = usePolygonZones();
    const {
        channelTypes: {
            data: channelTypesOptions,
            error: channelTypesError,
            reload: reloadChannelTypes,
            isLoading: channelTypesLoading,
        }
    } = useChannelTypes();

  
    const [currentStep, setCurrentStep] = useState<Step>({
        step: readOnly ? steps.travelStatus : steps.grouperAndServiceType,
        direction: StepDirection.NEXT
    });


    useEffect(() => {
        if (readOnly) {
        setCurrentStep({
            step: steps.travelStatus,
            direction: StepDirection.NEXT
        });
        }
    }, [readOnly]);

    const hasLoadError = !!(serviceTypesError || stagesError || polygonZonesError || channelTypesError);
    const isLoadingAny = serviceTypesLoading || stagesLoading || polygonZonesLoading || channelTypesLoading;

    const initialValues: RulesConfigurationFormType = initialData || {
        name: '',
        service_type_ids: [],
        is_moment: false,
        is_airport: false,
        is_priority: false,
        stages: [],
    };

    const handleNext = async (validateForm: () => Promise<any>, values: RulesConfigurationFormType, setTouched: (touched: any) => void) => {
        const errors = await validateForm();

        const hasErrorsInCurrentStep = Object.keys(errors).some(key => {
            if (currentStep.step === steps.grouperAndServiceType) {
                setTouched({
                    is_moment: true,
                    is_airport: true,
                    service_type_ids: true,
                });
                return ['is_moment', 'is_airport', 'service_type_ids'].includes(key);
            }
            return true;
        });

        if (Object.keys(errors).length === 0 || !hasErrorsInCurrentStep) {
            setCurrentStep({
                step: steps.travelStatus,
                direction: StepDirection.NEXT
            });
        }
    };

    const shouldRender = (step: steps) => {
        if (readOnly) {
            return step === steps.travelStatus;
        }
        const { step: active, direction } = currentStep;

        // Siempre renderizar el step activo.
        if (step === active) return true;

        // Mantener en el DOM el paso "saliente" para que la animación tenga continuidad.
        if (direction === StepDirection.NEXT) {
            return step === steps.grouperAndServiceType && active === steps.travelStatus;
        }

        return step === steps.travelStatus && active === steps.grouperAndServiceType;
    };



    const getAnimationClass = (isCurrentStep: boolean, direction: StepDirection) => {
        if (direction === StepDirection.NEXT) {
            if (isCurrentStep) {
                return 'animate-in-right';
            }
            return 'animate-out-left pointer-events-none';
        }
        if (isCurrentStep) {
            return 'animate-in-left';
        }
        return 'animate-out-right pointer-events-none';
    };

    const getStepClass = (step: steps) => clsx(
        "w-full h-full flex flex-col items-center",
        getAnimationClass(currentStep.step === step, currentStep.direction)
    );

    if (hasLoadError) {
        return (
            <div className="flex flex-col items-center justify-center gap-4 p-6 bg-white rounded-2xl shadow-lg border border-red-100 min-w-[500px]">
                <p className="text-red-700 font-semibold text-center">
                    Ocurrió un error al cargar los datos necesarios para el formulario.
                </p>
                <p className="text-sm text-gray-600 text-center">
                    Por favor, volvé a intentar cargar los datos.
                </p>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        reloadServiceTypes();
                        reloadStages();
                        reloadPolygonZones();
                        reloadChannelTypes();
                    }}
                    isLoading={isLoadingAny}
                    disabled={isLoadingAny}
                >
                    Reintentar cargar datos
                </Button>
            </div>
        );
    }

    return (
        <Formik<RulesConfigurationFormType>
            initialValues={initialValues}
            validationSchema={readOnly ? undefined : validationSchema}
            enableReinitialize
            onSubmit={async (values, helpers) => {
                if (onSubmit) {
                    await onSubmit(values);
                }
                helpers.setSubmitting(false);
            }}
        >
            {({ isSubmitting, validateForm, values, setTouched, dirty }) => (
                <Form className={clsx("flex gap-4 w-full h-full min-h-full overflow-visible", readOnly ? "justify-center" : "justify-between")}>
                    {!readOnly &&
                        <div className="flex justify-end items-center relative w-full min-h-full pr-4" style={{ minHeight: '540px' }}>
                            <Button
                                type="button"
                                variant="text"
                                className='text-secondary-500 hover:text-secondary-400 hover:bg-secondary-50'
                                color=""
                                isLoading={isSubmitting}
                                disabled={isSubmitting || (currentStep.step === steps.grouperAndServiceType)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setCurrentStep({ step: steps.grouperAndServiceType, direction: StepDirection.PREV });
                                }}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} size='2x' />
                            </Button>
                        </div>
                    }
                    <div className="flex justify-center items-start relative w-fit">
                        {shouldRender(steps.grouperAndServiceType) && (
                            <div className={getStepClass(steps.grouperAndServiceType)}>
                                <GrouperAndServiceTypeStep servicesOptions={servicesOptions} />
                            </div>
                        )}

                        {shouldRender(steps.travelStatus) && (
                            <div className={getStepClass(steps.travelStatus)}>
                                <TravelStatusStep
                                    stagesOfReserveStatesOptions={stagesOfReserveStatesOptions}
                                    polygonZonesOptions={polygonZonesOptions}
                                    channels={channelTypesOptions}
                                    readOnly={readOnly}
                                />
                            </div>
                        )}
                    </div>

                    {!readOnly &&
                    <div className="flex justify-between items-center relative w-full min-h-full pl-4 gap-4" style={{ minHeight: '540px' }}>
                        <Button
                        type="button"
                        variant="text"
                        className='text-secondary-500 hover:text-secondary-400 hover:bg-secondary-50'
                        color=""
                        isLoading={isSubmitting}
                        disabled={isSubmitting || (currentStep.step === steps.travelStatus)}
                        onClick={(e) => {
                            e.preventDefault();
                            void handleNext(validateForm, values, setTouched);
                        }}
                    >
                        <FontAwesomeIcon icon={faArrowRight} size='2x' />
                    </Button>
                    {currentStep.step === steps.travelStatus && <Button
                        type="submit"
                        variant="contained"
                        isLoading={isSubmitting}
                        disabled={isSubmitting || !dirty}
                    >
                        <Typography variant="subtitle1"> Finalizar </Typography>
                        <FontAwesomeIcon icon={currentStep.step === steps.travelStatus ? faCheck : faArrowRight} className='ml-2' size='1x' />
                    </Button> }
                    </div>
                    
                    }
                </Form>
            )}
        </Formik>
    )
}

export default RulesConfigurationForm